import { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import Spin from "../common/Spin";
import { MonthRangePicker } from "@semcore/date-picker";
import {
  checkIfFileExists,
  DDMMYYYYFormat,
  formatNumber,
  formatSubcontractorName,
  pastFinancialdate,
} from "../common/Misc";
import moment from "moment";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  fileUpload,
  get,
  imageDefaultPrefixPath,
} from "../../config";
import { toast } from "react-toastify";
import ProjectSelect from "../common/ProjectSelect";
import BuilderSelect from "../common/BuilderSelect";
import PageNotFound from "../PageNotFound";
import Select from "react-select";
import MixBarChart from "./MixBarChart";
import { Button, Dropdown, DropdownButton } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faCircleCheck,
  faCircleXmark,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import PaymentClaimStatus from "./payment-claim/components/PaymentClaimStatus";
import axios from "axios";
import Pagination from "../common/Pagination";

const ContactProjectDashboard = () => {
  const roleType = localStorage.getItem("roleType");
  const userId = localStorage.getItem("userId");
  const [loading, setLoading] = useState(false);
  const [valueRange, setValueRange] = useState(pastFinancialdate);
  const navigate = useNavigate();
  const params = useParams();
  const projectID = params?.project_id;
  const [projectId, setProjectId] = useState(projectID);

  const [contractId, setContractId] = useState();

  const [projectLogo, setProjectLogo] = useState();
  const [projectName, setProjectName] = useState();

  const [builderId, setBuilderId] = useState();
  const [builderData, setBuilderData] = useState();
  const [projectData, setProjectData] = useState();

  const [ptaRtaChartData, setPtaRtaChartData] = useState([]);
  const [progressChartData, setProgressChartData] = useState();

  const defaultPiChartData = [{ value: 100, name: "No Data", total: 0 }];
  const [subContractorPieChartData, setSubContractorPieChartData] =
    useState(defaultPiChartData);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [paymentClaimList, setPaymentClaimList] = useState();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  useEffect(() => {
    if (projectLogo && typeof projectLogo === "string") {
      checkIfFileExists(`${imageDefaultPrefixPath}${projectLogo}`, (exists) => {
        if (exists) {
          setProjectLogo(projectLogo);
        } else {
          setProjectLogo("");
        }
      });
    } else {
      setProjectLogo("");
    }
  }, [projectLogo]);

  const fetchDashboardDetails = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await get(
        `${
          API.GET_CONTACT_DASHBOARD
        }?contact_user_id=${userId}&builder_project_id=${projectId}&contract_id=${
          contractId ? contractId : ""
        }`
      );

      setBuilderId(data?.data?.builder?.builder?.id);

      setBuilderData(data?.data?.builder);
      setProjectData(data?.data?.project);
      setProjectLogo(data?.data?.project?.logo);
      setProjectName(data?.data?.project?.project_name);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setBuilderId();

      if (error?.response?.data?.errors) {
        Object.keys(error?.response?.data?.errors).forEach((key) => {
          toast.error(error?.response?.data?.errors[key][0]);
        });
      } else {
        toast.error(error?.message);
      }
    }
  }, [contractId, projectId, userId]);

  const fetchGraphData = useCallback(async () => {
    if (projectId) {
      try {
        setLoading(true);
        const { data } = await fileUpload(
          `${
            API.GET_CONTACT_DASHBOARD_GRAPH
          }?contact_user_id=${userId}&builder_project_id=${
            projectId ? projectId : ""
          }&from_month=${
            moment(valueRange[0]).format("MM-YYYY")
              ? moment(valueRange[0]).format("MM-YYYY")
              : ""
          }&to_month=${
            moment(valueRange[1]).format("MM-YYYY")
              ? moment(valueRange[1]).format("MM-YYYY")
              : ""
          }&contract_id=${contractId ? contractId : ""}`
          // formData
        );
        setLoading(false);

        const ptaRtaData = data?.data?.rtaPtaChart
          ? data?.data?.rtaPtaChart
          : [];
        setPtaRtaChartData(ptaRtaData);

        setProgressChartData(
          data?.data?.progressPaymentChartData
            ? data?.data?.progressPaymentChartData
            : ""
        );

        const expData =
          data?.data?.paymentClaimsChart &&
          data?.data?.paymentClaimsChart?.length > 0
            ? data?.data?.paymentClaimsChart?.map((item) => {
                const { percentage: value, ...rest } = item;
                return { value, ...rest };
              })
            : [];

        if (expData?.length > 0) {
          setSubContractorPieChartData(expData);
        } else {
          setSubContractorPieChartData(defaultPiChartData);
        }

        return data.data;
      } catch (e) {
        setLoading(false);
        const { data } = e;
        toast.error(
          data && data.errors && data.errors.bt_error && data.errors.bt_error[0]
        );
      }
    }
    // eslint-disable-next-line
  }, [projectID, valueRange, userId, contractId]);

  useEffect(() => {
    projectId && fetchGraphData();
  }, [projectId, fetchGraphData]);

  useEffect(() => {
    projectId && fetchDashboardDetails();
  }, [fetchDashboardDetails, projectId]);

  useEffect(() => {
    navigate(`/projects/${projectId}/contact`);
    // setProjectValue(projectId);
  }, [projectId, navigate]);

  const CustomExpRevTooltip = ({ active, payload, label }) => {
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "#ffff",
          padding: "5px",
          border: "1px solid #cccc",
        }}
      >
        <label>
          {payload && payload?.length > 0
            ? `${payload[0]?.payload?.month}`
            : ""}
        </label>
        <br />
        <label>
          {payload && payload?.length > 0
            ? `PTA : $${payload[0]?.payload?.PTA?.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            : ""}
        </label>
        <br />
        <label>
          {payload && payload?.length > 0
            ? `RTA : $${payload[0]?.payload?.RTA?.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            : ""}
        </label>
      </div>
    );
  };
  const CustomTooltipForExp = ({ active, payload, label }) => {
    if (active) {
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "#ffff",
            padding: "5px",
            border: "1px solid #cccc",
          }}
        >
          <label>
            {payload && payload?.length > 0
              ? `${payload[0].name} (${payload[0].value}%)`
              : ""}
          </label>
          <br />
          <label>
            {payload && payload?.length > 0
              ? payload[0]?.payload?.amount
                ? `$${payload[0]?.payload?.amount?.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : "$0.00"
              : "$0.00"}
          </label>
        </div>
      );
    }

    return null;
  };

  const monthRangeDisplay =
    moment(valueRange[0]).format("yyyy") ===
    moment(valueRange[1]).format("yyyy")
      ? moment(valueRange[0]).format("MMM") ===
        moment(valueRange[1]).format("MMM")
        ? `${moment(valueRange[0]).format("MMM")}, ${moment(
            valueRange[0]
          ).format("yyyy")}`
        : `${moment(valueRange[0]).format("MMM")}-${moment(
            valueRange[1]
          ).format("MMM")}, ${moment(valueRange[0]).format("yyyy")}`
      : `${moment(valueRange[0]).format("MMM")} ${moment(valueRange[0]).format(
          "yyyy"
        )} - ${moment(valueRange[1]).format("MMM")} ${moment(
          valueRange[1]
        ).format("yyyy")}`;

  let futureMonthDate = moment(valueRange[1]).add(3, "months").toISOString();

  const monthRangeDisplayBarGraph =
    moment(valueRange[0]).format("yyyy") ===
    moment(futureMonthDate).format("yyyy")
      ? moment(valueRange[0]).format("MMM") ===
        moment(futureMonthDate).format("MMM")
        ? `${moment(valueRange[0]).format("MMM")}, ${moment(
            valueRange[0]
          ).format("yyyy")}`
        : `${moment(valueRange[0]).format("MMM")}-${moment(
            futureMonthDate
          ).format("MMM")}, ${moment(valueRange[0]).format("yyyy")}`
      : `${moment(valueRange[0]).format("MMM")} ${moment(valueRange[0]).format(
          "yyyy"
        )} - ${moment(futureMonthDate).format("MMM")} ${moment(
          futureMonthDate
        ).format("yyyy")}`;

  const today = new Date();

  var disabledFutureDate = [];
  for (let i = 1; i < 1000; i++) {
    disabledFutureDate.push(
      new Date(today.getFullYear(), today.getMonth() + i, 1)
    );
  }

  const COLORS = [
    "#1F78B4",
    "#1B9BDF",
    "#034262",
    "#C7DAE3",
    "#A6CEE3",
    "#B2DF8A",
    "#d3f7b2",
    "#2b6a8a",
    "#6a9aba",
    "#0f71a3",
  ];

  const [projectValue1, setProjectValue1] = useState();
  const [builderValue1, setBuilderValue1] = useState();

  const [contractOptions, setContractOptions] = useState([]);

  const fetchContracts = useCallback(
    async (id) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_CONTRACTS}?contact_user_id=${userId}&builder_project_id=${id}&limit=-1&page=1`
        );
        const list =
          data &&
          data?.data?.data?.length > 0 &&
          data?.data?.data?.map((item) => {
            const list = {
              label: item?.contract_number,
              value: item?.id,
              ...item,
            };
            return list;
          });
        setContractOptions(list ? [...list] : []);
        list?.length > 0 && setContractId(list[0]?.value);
        setLoading(false);
        // const list = data.data;s
        // setContractOptions(list);
        return data.data;
      } catch (error) {
        setLoading(false);
        setContractOptions([]);

        if (error?.response?.data?.errors) {
          Object.keys(error?.response?.data?.errors).forEach((key) => {
            toast.error(error?.response?.data?.errors[key][0]);
          });
        } else {
          toast.error(error?.message);
        }
      }
    },
    [userId]
  );

  useEffect(() => {
    fetchContracts(projectId);
  }, [projectId, fetchContracts]);

  // const totalSub =
  //   subContractorPieChartData && subContractorPieChartData?.length > 0
  //     ? subContractorPieChartData?.map((item) => {
  //         return item?.amount ? parseFloat(item?.amount) : 0;
  //       })
  //     : [];
  // const totalSubContractorPieChartData =
  //   totalSub && totalSub?.length > 0
  //     ? totalSub.reduce((partialSum, a) => partialSum + a, 0)
  //     : 0;

  const columns = [
    {
      dataField: "post_date",
      text: "Posted Date",
      sort: true,
      style: {
        width: "100px",
      },
    },
    {
      dataField: "name",
      text: "Payment Claim By ",
      sort: true,
    },
    {
      dataField: "contract_number",
      text: "Contract / Agreement Number",
      // sort: true,
      style: {
        width: "10%",
      },
    },
    {
      dataField: "invoice_number",
      text: "Invoice/Claim Number",
      sort: true,
      style: {
        width: "11%",
      },
    },
    {
      dataField: "invoice_date",
      text: "Invoice/Claim Date",
      sort: true,
      style: {
        width: "100px",
      },
    },
    {
      dataField: "due_date",
      text: "Due Date",
      sort: true,
      style: {
        width: "110px",
      },
    },
    {
      dataField: "exc_gst_claimed_amount",
      text: "Claimed Amount (Ex. GST)",
      // sort: true,
    },
    {
      dataField: "claimed_amount",
      text: "Claimed Amount (Inc. GST)",
      // sort: true,
    },
    {
      dataField: "import_from",
      text: "Source",
      style: {
        width: "90px",
      },
    },
    {
      dataField: "status",
      text: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "3px",
          }}
        >
          <div className="apca-info info-text claim-status">
            <DropdownButton className="info-icon" title="">
              <Dropdown.Item>
                <div className="payment-claim-list">
                  <div className="payment-claim-status-text">
                    <FontAwesomeIcon
                      className="pending-status"
                      icon={faCircle}
                    />
                    <span>
                      Pending (Payment claim made but no action taken)
                    </span>
                  </div>
                  <div className="payment-claim-status-text">
                    <FontAwesomeIcon
                      className="in-progress-status"
                      icon={faClock}
                    />
                    <span>In Progress (Posted but not paid)</span>
                  </div>
                  <div className="payment-claim-status-text">
                    <FontAwesomeIcon
                      className="approved-status"
                      icon={faCircleCheck}
                    />
                    <span>Completed (Posted & Paid)</span>
                  </div>
                  <div className="payment-claim-status-text">
                    <FontAwesomeIcon
                      className="disapproved-status"
                      icon={faCircleXmark}
                    />
                    <span>Rejected (Payment Claim Rejected)</span>
                  </div>
                </div>
              </Dropdown.Item>
            </DropdownButton>
          </div>
          Status
        </div>
      ),
      // headerStyle: { width: "10%" },
      headerStyle: { width: "105px" },
    },
  ];

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchPaymnetClaims(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchPaymnetClaims(limit, page);
  };

  const fetchPaymnetClaims = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${
            API.GET_PAYMENT_CLAIM_LIST
          }?builder_project_id=${projectId}&limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${
            passPage ? passPage : page ? page : 1
          }&trust_type=${1}&sort_column=${
            sortField ? sortField : ""
          }&sort_order=${
            sortOrder ? sortOrder : ""
          }&status=${0}&contact_user_id=${
            roleType === "Contact" ? userId : ""
          }&from_month=${
            moment(valueRange[0]).format("MM-YYYY")
              ? moment(valueRange[0]).format("MM-YYYY")
              : ""
          }&to_month=${
            moment(valueRange[1]).format("MM-YYYY")
              ? moment(valueRange[1]).format("MM-YYYY")
              : ""
          }${
            contractId !== undefined && contractId
              ? `&contracts=${contractId}`
              : ""
          }`
        );
        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const paymentClaimList = data.data.data;
        setPaymentClaimList(paymentClaimList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setPaymentClaimList([]);

        if (error?.response?.data?.errors) {
          Object.keys(error?.response?.data?.errors).forEach((key) => {
            toast.error(error?.response?.data?.errors[key][0]);
          });
        } else {
          toast.error(error?.message);
        }
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, sortField, sortOrder, roleType, userId, valueRange, contractId]
  );

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  const downloadLetter = async (path) => {
    try {
      if (path) {
        const response = await axios.get(`${path}`, {
          responseType: "blob",
        });
        const fileData = response.data;
        const url = window.URL.createObjectURL(new Blob([fileData]));
        var a = document.createElement("a");
        a.href = url;
        var file = path.split("/");
        a.setAttribute("download", file[file.length - 1] || "new_aba.aba");
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        toast.error("File does not exist.");
      }
    } catch (e) {
      const errors = e.response?.data?.errors;
      Object.keys(errors).forEach((key) => {
        toast.error(errors[key][0]);
      });
    }
  };

  const paymentClaimGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: paymentClaimList[i]?.id,
        expandData: paymentClaimList[i]?.payment_claim_items
          ? paymentClaimList[i]?.payment_claim_items
          : [],
        name: formatSubcontractorName(paymentClaimList[i]?.contact),
        contract_number: paymentClaimList[i]?.contract?.contract_number
          ? paymentClaimList[i]?.contract?.contract_number
          : "-",
        invoice_number: paymentClaimList[i]?.invoice_number ? (
          paymentClaimList[i]?.file ? (
            <Link
              onClick={() => downloadLetter(paymentClaimList[i]?.file)}
              style={{
                textDecoration: "underline",
              }}
            >
              {paymentClaimList[i]?.invoice_number}
            </Link>
          ) : (
            paymentClaimList[i]?.invoice_number
          )
        ) : (
          "-"
        ),
        invoice_date: paymentClaimList[i]?.invoice_date
          ? DDMMYYYYFormat(paymentClaimList[i]?.invoice_date?.split("-"))
          : "",
        due_date: paymentClaimList[i]?.due_date
          ? DDMMYYYYFormat(paymentClaimList[i]?.due_date?.split("-"))
          : "",
        exc_gst_claimed_amount: paymentClaimList[i]?.total_claimed_amount
          ? `$${formatNumber(
              Number(
                formatNumber(
                  Number(paymentClaimList[i]?.total_claimed_amount)
                ).replace(/,/g, "") / 1.1
              )
            )}`
          : "$0.00",
        claimed_amount: paymentClaimList[i]?.total_claimed_amount
          ? `$${formatNumber(
              Number(paymentClaimList[i]?.total_claimed_amount)
            )}`
          : "$0.00",
        post_date: paymentClaimList[i]?.post_date
          ? DDMMYYYYFormat(paymentClaimList[i]?.post_date?.split("-"))
          : "-",
        import_from: paymentClaimList[i]?.import_from
          ? paymentClaimList[i]?.import_from
          : "BuildTrust",
        status: (
          <>
            <PaymentClaimStatus
              id={paymentClaimList[i]?.id}
              approve={paymentClaimList[i]?.status}
              email={
                paymentClaimList[i]?.contact?.email
                  ? paymentClaimList[i]?.contact?.email
                  : ""
              }
              fetchPaymnetClaims={() => fetchPaymnetClaims(limit, page)}
              projectId={projectId}
              trust_type={1}
              setSelectedRowIds={[]}
            />
          </>
        ),
        approve_status: `${paymentClaimList[i]?.status}`,
      });
    }
    return items;
  };

  const paymentClaims = paymentClaimGenerator(paymentClaimList?.length);

  useEffect(() => {
    fetchPaymnetClaims();
  }, [fetchPaymnetClaims]);

  return roleType !== "Contact" ? (
    <PageNotFound />
  ) : (
    <div className="cms-page">
      <div className="page-content-block">
        <div className="full-content-block">
          <div className="page-title">
            <>
              <BuilderSelect
                regionName={"0"}
                onChange={(e) => {
                  setBuilderId(e?.builder?.id);
                  setBuilderValue1(e);
                  setProjectValue1("");
                }}
                value={builderValue1 ? builderValue1 : null}
                placeholder={"Select Builder"}
                isClearable={false}
                defaultValue={{
                  first_name: builderData?.first_name,
                  last_name: builderData?.last_name,
                  builder: { id: builderId },
                }}
              />

              <ProjectSelect
                regionName={builderId ? builderId : "0"}
                onChange={(e) => {
                  setProjectId(e?.id);
                  setProjectValue1(e);
                }}
                value={projectValue1 ? projectValue1 : null}
                placeholder={"Select Project"}
                builderId={builderId}
                isClearable={false}
                defaultValue={
                  builderId === projectData?.builder_id && {
                    project_name: projectName,
                    id: projectId,
                  }
                }
              />

              <Select
                options={contractOptions}
                onChange={async (e) => {
                  setContractId(e?.value);
                }}
                value={
                  contractId
                    ? contractOptions?.find((item) => item.value === contractId)
                    : ""
                }
                defaultValue={
                  contractOptions?.length > 0 ? contractOptions[0]?.value : ""
                }
                isClearable
                placeholder="Select Contract"
                name="contract"
                classNamePrefix="selectbox"
              />
            </>

            <>
              <div className="summary-filter">
                <MonthRangePicker
                  value={valueRange}
                  onChange={(date) => {
                    setValueRange(date);
                  }}
                  className="datefilter"
                  disabled={disabledFutureDate}
                />
              </div>
            </>
            <div className="page-title-right">
              <Button
                variant="primary"
                onClick={() =>
                  navigate(`/projects/${projectId}/payment-claims?tab=input`, {
                    replace: true,
                  })
                }
              >
                Make a Claim
              </Button>
              {projectLogo && (
                <img src={`${imageDefaultPrefixPath}${projectLogo}`} alt="" />
              )}{" "}
              {projectName}
            </div>
          </div>
          <div className="dashboard-container" style={{ width: "100%" }}>
            {loading && <Spin />}

            <div className="row">
              <div className="charts-container row" style={{ width: "100%" }}>
                {/* Bar Chart Card */}
                <div className="card col-lg-6 col-sm-12">
                  <div className="card-content">
                    <div className="card-body first-chart">
                      <div className="card-header mb-2">
                        <h5>
                          Payment Made From Project Trust and to Retention Trust
                        </h5>
                        <div style={{ marginLeft: "auto" }}>
                          <strong>Period: </strong>
                          {monthRangeDisplayBarGraph}
                        </div>
                      </div>
                      <div className="coa-charts">
                        <ResponsiveContainer width="100%" height={380}>
                          <BarChart
                            data={ptaRtaChartData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 45 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="month"
                              angle={-40}
                              textAnchor="end"
                            />
                            <YAxis />
                            <Tooltip content={<CustomExpRevTooltip />} />
                            <Legend
                              layout="horizontal"
                              verticalAlign="bottom"
                              align="center"
                              wrapperStyle={{
                                bottom: 0,
                              }}
                            />
                            <Bar dataKey="PTA" barSize={20} fill="#012F4E" />
                            <Bar dataKey="RTA" barSize={20} fill="#0DCAF0" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pie Chart Card */}
                <div className="card col-lg-6 col-sm-12">
                  <div className="card-content">
                    <div className="card-body first-chart pie-chart">
                      <div className="card-header mb-2">
                        <h5>Payment Claim Status</h5>
                        <div style={{ marginLeft: "auto" }}>
                          <strong>Period: </strong> {monthRangeDisplay}
                        </div>
                      </div>
                      <div className="coa-charts">
                        <ResponsiveContainer width="100%" height={380}>
                          <PieChart>
                            <Pie
                              data={subContractorPieChartData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={3}
                              outerRadius="78%"
                              label={({
                                cx,
                                cy,
                                midAngle,
                                innerRadius,
                                outerRadius,
                                value,
                                index,
                              }) => {
                                const RADIAN = Math.PI / 180;
                                const radius =
                                  innerRadius * 8 + (outerRadius - innerRadius);
                                const x =
                                  cx + radius * Math.cos(-midAngle * RADIAN);
                                const y =
                                  cy + radius * Math.sin(-midAngle * RADIAN);
                                return (
                                  <text
                                    x={x}
                                    y={y}
                                    fill="#000"
                                    textAnchor={x > cx ? "start" : "end"}
                                    dominantBaseline="central"
                                  >
                                    {value ? `${value}%` : "0.00"}
                                  </text>
                                );
                              }}
                            >
                              {subContractorPieChartData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltipForExp />} />
                            <Legend
                              layout="vertical"
                              verticalAlign="middle"
                              align="right"
                              iconSize={15}
                              wrapperStyle={{
                                padding: "0px",
                                fontSize: "12px",
                                maxHeight: "270px",
                                overflow: "auto",
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        {/* <div
                          style={{
                            // marginTop: "-17px",
                            float: "left",
                            width: "45%",
                            // textAlign: "center",
                          }}
                        >
                          <p>
                            <strong>Total : </strong>$
                            {totalSubContractorPieChartData?.toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </p>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>

                {contractId && (
                  <div className="card col-lg-12 col-sm-12">
                    <div className="card-content">
                      <div
                        className="card-body"
                        style={{
                          height: "fit-content",
                        }}
                      >
                        <div className="card-header mb-2">
                          <h5>
                            Progress Payment - (
                            {
                              contractOptions?.find(
                                (item) => item?.value === contractId
                              )?.label
                            }
                            )
                          </h5>
                          {/* <div style={{ marginLeft: "auto" }}>
                            <strong>Period: </strong> {monthRangeDisplay}
                          </div> */}
                        </div>
                        <div className="coa-charts">
                          <ResponsiveContainer width="100%" height={100}>
                            <MixBarChart data={progressChartData} />
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="card col-lg-12 col-sm-12">
                  <div className="card-content">
                    <div
                      className="card-body"
                      style={{
                        height: "fit-content",
                      }}
                    >
                      <div className="card-header mb-2">
                        <h5>Pending Claims</h5>
                        <div style={{ marginLeft: "auto" }}>
                          <strong>Period: </strong> {monthRangeDisplay}
                        </div>
                      </div>
                      <div className="coa-charts">
                        <div
                          className="custom-table tab-table payment-claim-list"
                          style={{
                            width: "100%",
                          }}
                        >
                          {loading && <Spin />}
                          <BootstrapTable
                            keyField="key"
                            remote
                            data={paymentClaims || []}
                            columns={columns}
                            onTableChange={handleTableChange}
                            noDataIndication="No Data Found"
                          />

                          <Pagination
                            total={total}
                            limit={parseInt(limit)}
                            currentPage={page}
                            updateLimit={handleUpdateLimit}
                            updatePage={handleChangePage}
                            from={from}
                            to={to}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactProjectDashboard;
